import { useId, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { line, Color, FieldSize, NEVER_NOT_APPLICABLE, Size, t, type SubStage } from '@bms2/shared';
import { Button, Checkbox, DatePicker, Textarea } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { NoteComposer } from '../../components/composites/NoteComposer';
import styles from './StageCard.module.css';

export type StageCardState = 'current' | 'done' | 'todo';

/** A row whose answer is a list nested under it, in place of a note (decision 0332). */
export interface StageCardNested {
  label: string;
  count: number;
  content: ReactNode;
  /**
   * 🔒 The block draws open on the first render (decision 0595). A row that
   * holds the controls the list is made with cannot hide them behind a press:
   * the expander stays, so the block still folds away, and it starts unfolded.
   */
  startOpen?: boolean;
}

/** A row whose answer is an uploaded file, in place of a note (decision 0332). */
export interface StageCardFile {
  fileId: string | null;
  fileName: string | null;
  href: string | null;
  onFiles: (files: readonly File[]) => void;
  onRemove: () => void;
  /** What the drop box asks for; the Lead's signed agreement and the Deal's negotiated terms are not the same ask. */
  uploadLabel?: string;
  uploadHint?: string;
  /**
   * 🔒 The row is answered by the file or by a typed note (decision 2091): until a
   * file answers it, it keeps its checkbox and its note box above the upload box.
   */
  orNote?: boolean;
}

export interface StageCardRowView {
  key: string;
  label: string;
  placeholder: string;
  checked: boolean;
  notes: string | null;
  applicable: boolean;
  /** A row the system answers, never a person: the NDA read off the Company, a Won row a document writes. */
  derived: boolean;
  derivedText?: string | null;
  dueDate?: string | null;
  nested?: StageCardNested | null;
  file?: StageCardFile | null;
  /** What the row carries beneath its note box: the Deal's Authority row draws the people who decide there (decision 0618). */
  extra?: ReactNode;
  /**
   * 🔒 A row whose answer the page draws in place of the note box: the Deal's
   * Budget pair (decision 0664). The tick follows the answer, the way it follows
   * a file, so the row draws no checkbox.
   */
  answer?: ReactNode;
  /** 🔒 What turns the row green, in one line under its name (decision 0724): the Deal's Intent rows carry one. */
  rule?: string | null;
  /**
   * 🔒 The row's tagged notes (decision 0725): the Deal's rows keep every note
   * written as a history. The notes composer stands above it and adds the next
   * with Add (decision 0910). A row with an answer draws both after it.
   */
  section?: ReactNode;
}

/** The middle dot between two facts of a card's head (decision 1216). */
const FACT_SEPARATOR = '\u00b7';

export interface StageCardProps {
  stage: string;
  title?: string;
  subtitle?: string | null;
  counter: string;
  state: StageCardState;
  rows: readonly StageCardRowView[];
  /** A card the record has reached takes ticks and notes; a card not yet reached reads (decision 0331). */
  editable: boolean;
  notesLabel: string;
  onCheck?: (key: string, checked: boolean, notes: string | null) => void;
  /** A writer that answers with a promise says whether the save held; a row's composer empties only on true (decisions 0725, 0910). */
  onNotes?: (key: string, notes: string | null) => Promise<boolean> | void;
  onDue?: (key: string, due: string | null) => void;
  onApplicable?: (key: string, applicable: boolean) => void;
  /** Back to the stage behind this one, on the current card alone (decision 0331). */
  onBack?: (() => void) | null;
  backLabel?: string | null;
  foot?: ReactNode;
  note?: string | null;
  data?: Record<string, string>;
  /** 🔒 The card's one Age, drawn in its head beside the counter: the Deal's Intent card (decision 0719). */
  age?: string | null;
  /** 🔒 A help control in the card's head, before its facts (decision 0724): the Intent card's four row topics. */
  help?: ReactNode;
  /**
   * 🔒 A card that folds to one line (decision 1216, amending 0723): the Deal's
   * finished cards start folded on every open and the current one draws in
   * full. Show opens it in place and Hide folds it back. The page holds the
   * choice, so a reload after a write keeps an opened card open, and nothing
   * outlives the visit. Absent, the card does not fold.
   */
  fold?: { folded: boolean; onToggle: () => void } | null;
  /** 🔒 Every applicable row of the stage is done, so its head carries the tick (decision 0723). */
  complete?: boolean;
  /** 🔒 The counter as the head reads it, such as 4 of 4 done (decision 1216); absent, the counter reads as given. */
  counterLabel?: string | null;
  /** 🔒 The head's first fact, before the counter: a finished card's Completed and its date (decision 1216). */
  lead?: string | null;
  /** 🔒 The head's facts after the counter: the current card's days at this stage (decision 1216). */
  trail?: readonly string[];
  /** 🔒 Facts only the folded line carries: a finished card's lists, such as 2 bills of materials (decision 1216). */
  foldedTrail?: readonly string[];
  /** A pill beside the card's name: the current card's Now (decision 1216). */
  badge?: ReactNode;
  /** 🔒 Back to the stage behind stands in the head beside the facts, not in the foot (decision 1216). */
  backInHead?: boolean;
  /** 🔒 What a card draws under its rows in place of them: the Discovery card's sections (decision 5450). */
  body?: ReactNode;
  /**
   * 🔒 The words of a card with nothing recorded, read after its name on the head's line: Intent · nothing
   * recorded yet (decision 6702). The counter is not drawn beside them.
   */
  empty?: string | null;
  /** 🔒 What a read row with nothing on it says after its name, Not recorded, in place of N/A (decision 6702). */
  emptyRow?: string | null;
}

/**
 * The one StageCard construction (pieces 19 and 20; figure 82): a checkbox, a
 * bold label, an example-prompted notes box beneath it and the stage's
 * counter at the head. A note is written when its box loses focus, so there is
 * no Save button per question; a tick writes at once. 🔨 A question completes
 * only checked over a non-empty note, which is the server's rule and not the
 * card's; the card shows what is missing beside the stage's own button. A row
 * may answer with a nested list or an uploaded file instead of a note
 * (decision 0332), and a completed card stays editable (decision 0331). 🔒 The
 * notes box is the multi-line one and a note drawn read-only keeps the line
 * breaks its writer typed (decision 0619).
 */
export function StageCard({ stage, title, subtitle = null, counter, state, rows, editable, notesLabel, onCheck, onNotes, onDue, onApplicable, onBack = null, backLabel = null, foot, note, data = {}, age = null, fold = null, complete = false, help = null, counterLabel = null, lead = null, trail = [], foldedTrail = [], badge = null, backInHead = false, body = null, empty = null, emptyRow = null }: StageCardProps) {
  const folded = fold?.folded === true;
  const back = onBack && backLabel ? (
    <span className={styles.back} data-stage-card-back={stage}>
      <Button size={backInHead ? Size.Small : Size.Medium} color={Color.Inactive} text={[backLabel]} actions={[onBack]} />
    </span>
  ) : null;
  // The head's facts in reading order, a middle dot between each (decision 1216).
  const facts: { key: string; node: ReactNode }[] = [
    // A folded card's one line reads Completed, its count and its lists; the Intent card's Age and help wait for Show (decision 1216).
    ...(age && !folded ? [{ key: 'age', node: <span className={styles.age} data-stage-card-age={stage}>{age}</span> }] : []),
    ...(lead ? [{ key: 'lead', node: <span className={styles.age} data-stage-card-completed={stage}>{lead}</span> }] : []),
    // 🔒 No counter on a card with nothing recorded, and none that reads 0/0 or 0 of 0 done (decision 6702).
    ...(empty || (counterLabel ?? counter) === '' ? [] : [{ key: 'counter', node: <span className={styles.counter} data-stage-card-counter={counter}>{counterLabel ?? counter}</span> }]),
    ...trail.map((text, at) => ({ key: `trail-${at}`, node: <span className={styles.age} data-stage-card-fact={stage}>{text}</span> })),
    ...(folded ? foldedTrail.map((text, at) => ({ key: `folded-${at}`, node: <span className={styles.age} data-stage-card-list-count={stage}>{text}</span> })) : []),
  ];
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [dues, setDues] = useState<Record<string, string>>({});
  // A block that starts open is open on the first render and folds from there; the state is keyed by row.
  const [open, setOpen] = useState<Record<string, boolean>>(() => Object.fromEntries(rows.filter((row) => row.nested?.startOpen === true).map((row) => [row.key, true])));
  return (
    <section className={styles.card} data-stage-card={stage} data-stage-card-state={state} data-stage-card-folded={folded ? 'true' : 'false'} data-stage-card-current={state === 'current' ? 'true' : 'false'} data-stage-card-editable={editable ? 'true' : 'false'} aria-label={title ?? stage} {...data}>
      <header className={styles.head}>
        <span className={styles.title}>
          {/* 🔒 A completed stage's head carries its tick, folded or open (decision 0723). */}
          {complete ? <span className={styles.headTick} data-stage-card-tick={stage}><Icon meaning="confirmed" size="inline" /></span> : null}
          {title ?? stage}
          {badge ? <span className={styles.badge} data-stage-card-badge={stage}>{badge}</span> : null}
          {empty ? <span className={styles.subtitle} data-stage-card-empty={stage}>{line('·', empty)}</span> : null}
          {subtitle && !folded ? <span className={styles.subtitle}>{subtitle}</span> : null}
        </span>
        <span className={styles.headFacts}>
          {help && !folded ? <span className={styles.headHelp} data-stage-card-help={stage}>{help}</span> : null}
          {facts.map((fact, at) => (
            <span key={fact.key} className={styles.fact}>
              {at > 0 ? <span className={styles.dot} aria-hidden="true">{FACT_SEPARATOR}</span> : null}
              {fact.node}
            </span>
          ))}
          {backInHead ? back : null}
          {fold ? (
            /* 🔒 Show opens a finished card in place and Hide folds it (decision 1216). */
            <span className={styles.headFold} data-stage-card-fold={stage}>
              <Button size={Size.Small} text={[t(folded ? 'deal.card.show' : 'deal.card.hide')]} actions={[() => fold.onToggle()]} />
            </span>
          ) : null}
        </span>
      </header>
      {/* 🔨 Folded leaves the one line standing with its tick, its facts and Show; opening it draws the rows exactly as before (decisions 0723, 1216). */}
      {folded ? null : (<>
      <ul className={styles.rows}>
        {rows.map((row) => {
          const notes = drafts[row.key] ?? row.notes ?? '';
          const due = dues[row.key] ?? row.dueDate ?? '';
          const commitNotes = () => {
            if (!onNotes || (row.notes ?? '') === notes) return;
            void onNotes(row.key, notes || null);
          };
          /**
           * 🔒 A row with a history adds its next note from the notes composer, with Add (decision 0910,
           * amending 0725): the composer empties once the save holds and keeps the text when it does not.
           */
          const addSectionNote = async (text: string): Promise<boolean> => {
            if (!onNotes) return false;
            const saved = onNotes(row.key, text);
            return saved ? saved : true;
          };
          const expanded = open[row.key] === true;
          const box = editable && row.applicable ? (
            <div className={styles.rowBody} data-with-due={onDue && !row.answer ? 'true' : 'false'}>
              {/* 🔒 The note is a paragraph, so its box is the multi-line one (decision 0619): two lines to start, growing with what is typed to the control's own six-line cap.
                  🔒 It fills its row's width (decision 0718): a note box stopped at a form's Medium left most of a wide card empty beside it.
                  🔒 A row with a history draws the notes composer instead, one line to start with Add at its right (decision 0910). */}
              {row.section !== undefined ? (
                <NoteComposer name={`notes-${row.key}`} placeholder={row.placeholder} onAdd={addSectionNote} />
              ) : (
                <Textarea size={FieldSize.Column} label={notesLabel} labelHidden value={notes} onChange={(next) => setDrafts((held) => ({ ...held, [row.key]: next }))} onBlur={commitNotes} name={`notes-${row.key}`} placeholder={row.placeholder} rows={2} />
              )}
              {onDue && !row.answer ? <DatePicker size={Size.Small} label={t('deal.card.due')} value={due} onChange={(next) => { setDues((held) => ({ ...held, [row.key]: next })); onDue(row.key, next || null); }} mode="date" name={`due-${row.key}`} /> : null}
            </div>
          ) : null;
          // 🔒 A row with an answer and a history draws the composer in the answer's split, stacked under the values (decisions 5490, 10450).
          const split = Boolean(row.answer) && row.section !== undefined && box !== null;
          const body = row.nested ? (
            <NestedRow rowKey={row.key} nested={row.nested} expanded={expanded} />
          ) : row.file?.orNote && !row.file.fileId ? (
            // 🔒 A row answered by a note or a file draws its note box above the upload box until a file answers it (decision 2091).
            <>{box ? box : row.notes ? <p className={styles.readNotes}>{row.notes}</p> : null}<FileRow rowKey={row.key} file={row.file} editable={editable && row.applicable} /></>
          ) : row.file ? (
            <FileRow rowKey={row.key} file={row.file} editable={editable && row.applicable} />
          ) : row.answer ? (
            <div className={split ? styles.rowSplit : undefined} data-stage-question-split={split ? row.key : undefined}>
              <div className={styles.rowAnswer} data-stage-question-answer={row.key}>
                {row.answer}
                {/* The row's due date stays beside its answer, because Next Action reads it whatever the answer is (decision 0664). */}
                {onDue && editable && row.applicable ? <DatePicker size={Size.Small} label={t('deal.card.due')} value={due} onChange={(next) => { setDues((held) => ({ ...held, [row.key]: next })); onDue(row.key, next || null); }} mode="date" name={`due-${row.key}`} /> : null}
              </div>
              {/* 🔒 The note box stands under the answer's values at every width (decision 10450). */}
              {split ? <div className={styles.rowSplitNote}>{box}</div> : null}
            </div>
          ) : row.derived ? (
            <p className={styles.readNotes}>{row.notes ?? row.placeholder}</p>
          ) : row.section !== undefined ? (
            // 🔒 A row with a history draws the composer at the top of it (decision 0910, amending 0725).
            <>{box}{row.section}</>
          ) : box ? box : row.notes ? (
            <p className={styles.readNotes}>{row.notes}</p>
          ) : null;
          return (
            <li key={row.key} className={styles.row} data-stage-question={row.key} data-stage-question-checked={row.checked ? 'true' : 'false'} data-stage-question-applicable={row.applicable ? 'true' : 'false'}>
              <div className={styles.rowHead}>
                {row.derived ? (
                  <span className={styles.derived}>
                    <Icon meaning={row.checked ? 'confirmed' : 'indeterminate'} size="inline" />
                    {row.label}
                    {row.derivedText ? <span className={styles.derivedNote}>{line('·', row.derivedText)}</span> : null}
                  </span>
                ) : editable && row.applicable && (!row.file || (row.file.orNote && !row.file.fileId)) && !row.answer ? (
                  <Checkbox label={row.label} checked={row.checked} size={Size.Medium} onChange={(checked) => onCheck?.(row.key, checked, notes || null)} />
                ) : (
                  <span className={styles.readRow} data-checked={row.checked ? 'true' : 'false'}>
                    {row.checked ? <Icon meaning="confirmed" size="inline" /> : row.applicable ? <span className={styles.readRing} aria-hidden="true" /> : <Icon meaning="indeterminate" size="inline" />}
                    {emptyRow && !row.checked && !row.notes ? line(row.label, '·', emptyRow) : row.applicable ? row.label : line(row.label, `· ${t('deal.progress.na')}`)}
                  </span>
                )}
                <span className={styles.rowTools}>
                  {row.nested ? (
                    <button type="button" className={styles.linkButton} aria-expanded={expanded ? 'true' : 'false'} onClick={() => setOpen((held) => ({ ...held, [row.key]: !expanded }))} data-stage-question-expander={row.key}>
                      {line(expanded ? t('deal.card.hide') : t('deal.card.show'), row.nested.label, `(${row.nested.count})`)}
                      <Icon meaning={expanded ? 'collapse' : 'expand'} size="inline" />
                    </button>
                  ) : null}
                  {/* 🔒 Who asked and the need are never marked not applicable (decision 10690; T1005): the toggle is not drawn on them, unless a row already stands not applicable and must be brought back. */}
                  {editable && onApplicable && !row.derived && !(row.applicable && NEVER_NOT_APPLICABLE.includes(row.key as SubStage)) ? (
                    <button type="button" className={styles.linkButton} onClick={() => onApplicable(row.key, !row.applicable)} data-stage-question-applicable-toggle={row.key}>
                      {row.applicable ? t('deal.card.notApplicable') : t('deal.card.applicable')}
                    </button>
                  ) : null}
                </span>
              </div>
              {row.rule && row.applicable ? <p className={styles.rule} data-stage-question-rule={row.key}>{row.rule}</p> : null}
              {body}
              {/* 🔒 A row answered in place of its box keeps its history, with the composer at its top, beneath the answer (decisions 0725, 0910). */}
              {row.answer && row.section !== undefined ? <>{split ? null : box}{row.section}</> : null}
              {row.extra ? <div className={styles.rowExtra} data-stage-question-extra={row.key}>{row.extra}</div> : null}
            </li>
          );
        })}
      </ul>
      {body ? <div className={styles.body} data-stage-card-body={stage}>{body}</div> : null}
      {note ? <p className={styles.note}>{note}</p> : null}
      {foot || (onBack && !backInHead) ? (
        <div className={styles.foot}>
          {backInHead ? null : back}
          {foot}
        </div>
      ) : null}
      </>)}
    </section>
  );
}

/** The expander under a list-backed row: the row's own list, folded until asked for (decision 0332). */
function NestedRow({ rowKey, nested, expanded }: { rowKey: string; nested: StageCardNested; expanded: boolean }) {
  return (
    <div className={styles.nested} data-stage-question-nested={rowKey} data-stage-question-expanded={expanded ? 'true' : 'false'} hidden={!expanded}>
      {nested.content}
    </div>
  );
}

/** The upload box under a file-answered row: a native file input with drop events added, the file's name as a link once it is there (decision 0332). */
function FileRow({ rowKey, file, editable }: { rowKey: string; file: StageCardFile; editable: boolean }) {
  const inputId = useId();
  const [over, setOver] = useState(false);
  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length > 0) file.onFiles(files);
  };
  const drop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setOver(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) file.onFiles(files);
  };
  if (file.fileId) {
    return (
      <div className={styles.fileRow} data-stage-question-file={rowKey} data-stage-question-file-id={file.fileId}>
        <Icon meaning="file" size="inline" />
        {file.href ? <a className={styles.fileLink} href={file.href} target="_blank" rel="noreferrer">{file.fileName ?? file.fileId}</a> : <span className={styles.fileLink}>{file.fileName ?? file.fileId}</span>}
        {editable ? (
          <>
            <label className={styles.linkButton} htmlFor={inputId}>{t('lead.card.replaceFile')}</label>
            <input id={inputId} className={styles.fileInput} type="file" onChange={pick} name={`file-${rowKey}`} />
            <button type="button" className={styles.linkButton} onClick={file.onRemove} data-stage-question-file-remove={rowKey}>{t('lead.card.removeFile')}</button>
          </>
        ) : null}
      </div>
    );
  }
  if (!editable) return null;
  return (
    <div
      className={styles.dropBox}
      data-stage-question-file={rowKey}
      data-stage-question-file-over={over ? 'true' : 'false'}
      onDragOver={(event) => { event.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <Icon meaning="uploadSlot" size="inline" />
      <label className={styles.dropLabel} htmlFor={inputId}>{file.uploadLabel ?? t('lead.card.upload')}</label>
      <span className={styles.dropHint}>{file.uploadHint ?? t('lead.card.uploadHint')}</span>
      <input id={inputId} className={styles.fileInput} type="file" onChange={pick} name={`file-${rowKey}`} />
    </div>
  );
}

/** The list a list-backed row nests: the columns the row's list carries as a grid of rows, never a bare table (piece 4), and the empty text when it has none (decision 0332). */
export function NestedTable({ columns, rows, empty }: { columns: readonly { key: string; label: string; numeric?: boolean }[]; rows: readonly { id: string; cells: Record<string, string | null> }[]; empty: string }) {
  if (rows.length === 0) return <p className={styles.nestedEmpty}>{empty}</p>;
  const template = { gridTemplateColumns: columns.map((column) => (column.numeric ? 'max-content' : 'minmax(0, auto)')).join(' ') };
  return (
    <ul className={styles.nestedList} style={template}>
      <li className={styles.nestedHead} aria-hidden="true">
        {columns.map((column) => <span key={column.key} className={styles.nestedCell} data-numeric={column.numeric ? 'true' : 'false'}>{column.label}</span>)}
      </li>
      {rows.map((row) => (
        <li key={row.id} className={styles.nestedRow} data-nested-row={row.id}>
          {columns.map((column) => (
            <span key={column.key} className={styles.nestedCell} data-numeric={column.numeric ? 'true' : 'false'}>
              <span className={styles.nestedCellLabel}>{column.label}</span>
              {row.cells[column.key] ?? '—'}
            </span>
          ))}
        </li>
      ))}
    </ul>
  );
}

/** The foot's own hint: what still blocks the stage, beside its button. */
export function StageBlocked({ problems, prefix }: { problems: readonly string[]; prefix: string }) {
  if (problems.length === 0) return null;
  // 🔒 One sentence per row, drawn as a list and not a semicolon run (decision 10745; T980).
  return (
    <div className={styles.blocked} data-stage-card-problems={String(problems.length)}>
      <p className={styles.blockedLead}>{prefix}</p>
      <ul className={styles.blockedList}>
        {problems.map((problem) => <li key={problem}>{problem}</li>)}
      </ul>
    </div>
  );
}
